import React, { useState } from 'react';
import licenseService from '../services/licenseService';

const defaultExpiry = () => {
  const d = new Date();
  d.setFullYear(d.getFullYear() + 1);
  return d.toISOString();
};

function LicenseActivationSigner() {
  const [challengeBlob, setChallengeBlob] = useState('');
  const [productId, setProductId] = useState('');
  const [instanceId, setInstanceId] = useState('');
  const [seats, setSeats] = useState(1);
  const [expiry, setExpiry] = useState(defaultExpiry());
  const [features, setFeatures] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const featureArr = features
        ? features.split(',').map((f) => f.trim()).filter(Boolean)
        : [];
      const res = await licenseService.signActivation({
        challengeBlob,
        productId,
        instanceId,
        seats: Number(seats),
        expiry,
        features: featureArr
      });
      setResult(res);
    } catch (err) {
      setError(err?.message || 'Failed to sign activation');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white text-gray-900 rounded-lg shadow p-6">
      <h2 className="text-2xl font-semibold mb-4">License Activation Signer</h2>
      <p className="mb-4 text-sm text-gray-600">
        Paste a challenge blob from an on-prem License Manager and issue an activation response. Only product admins should use this.
      </p>
      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <label htmlFor="las-challenge-blob" className="block text-sm font-medium text-gray-700">Challenge Blob (base64)</label>
          <textarea
            id="las-challenge-blob"
            className="mt-1 w-full border rounded p-2 font-mono text-xs"
            rows={4}
            value={challengeBlob}
            onChange={(e) => setChallengeBlob(e.target.value)}
            required
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="las-product-id" className="block text-sm font-medium text-gray-700">Product ID</label>
            <input
              id="las-product-id"
              className="mt-1 w-full border rounded p-2"
              value={productId}
              onChange={(e) => setProductId(e.target.value)}
              required
            />
          </div>
          <div>
            <label htmlFor="las-instance-id" className="block text-sm font-medium text-gray-700">Instance ID</label>
            <input
              id="las-instance-id"
              className="mt-1 w-full border rounded p-2"
              value={instanceId}
              onChange={(e) => setInstanceId(e.target.value)}
              required
            />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label htmlFor="las-seats" className="block text-sm font-medium text-gray-700">Seats</label>
            <input
              id="las-seats"
              type="number"
              min="1"
              className="mt-1 w-full border rounded p-2"
              value={seats}
              onChange={(e) => setSeats(e.target.value)}
              required
            />
          </div>
          <div className="md:col-span-2">
            <label htmlFor="las-expiry" className="block text-sm font-medium text-gray-700">Expiry (ISO)</label>
            <input
              id="las-expiry"
              className="mt-1 w-full border rounded p-2"
              value={expiry}
              onChange={(e) => setExpiry(e.target.value)}
              required
            />
          </div>
        </div>
        <div>
          <label htmlFor="las-features" className="block text-sm font-medium text-gray-700">Features (comma separated)</label>
          <input
            id="las-features"
            className="mt-1 w-full border rounded p-2"
            value={features}
            onChange={(e) => setFeatures(e.target.value)}
          />
        </div>
        <div className="flex items-center gap-4">
          <button
            type="submit"
            disabled={loading}
            className="bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700 disabled:opacity-50"
          >
            {loading ? 'Signing…' : 'Sign activation'}
          </button>
          {error && <span className="text-sm text-red-600">{error}</span>}
        </div>
      </form>

      {result && (
        <div className="mt-6">
          <h3 className="text-lg font-semibold mb-2">Activation Response</h3>
          <pre className="bg-gray-100 border rounded p-3 overflow-x-auto text-xs">
{JSON.stringify(result, null, 2)}
          </pre>
          <div className="mt-2 text-sm text-gray-700">
            POST this JSON to your on-prem LM at <code>/license/activate</code>.
          </div>
        </div>
      )}
    </div>
  );
}

export default LicenseActivationSigner;
